import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { feedbackRef, type Workspace } from "@salon-pos/shared";
import { api } from "../api";
import { useAuth } from "../auth";
import { useI18n } from "../i18n";
import { errorText } from "../ui";
import { HelpText } from "./AiAssistant";
import { linkAllowed } from "./aiBus";
import { feedbackPath } from "../nav";
import { canSeeFeedback, feedbackChanged } from "../lib/feedback";
import "./hh-ai.css";

/**
 * Task HH: the floating "Senvia 助手" — Senvia's own support assistant (platform AI; no shop key needed). It answers
 * how-to questions from the help centre, asks clarifying questions when the request is vague, links to the right page,
 * and offers "联系人工客服" (Task NN: opens a 投诉与建议 ticket of type 问题回报 with the chat transcript). It never sees or changes the shop's business data:
 * the server only gets the current page, the user's role, the plan and the enabled features.
 * Without the platform key the button shows "暂未开放".
 */
interface Status { available: boolean; reason: "no_key" | "disabled" | null; remainingToday: number; dailyCap: number }
type Msg = { role: "user"; text: string } | { role: "assistant"; text: string; links?: Array<{ id: string; title: string; route: string }>; clarifying?: boolean } | { role: "error"; text: string };

export function SupportAssistant({ ws }: { ws: Workspace }) {
  const { me } = useAuth();
  const { t, locale } = useI18n();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<Status | null>(null);
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [conv, setConv] = useState<string | undefined>();
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [human, setHuman] = useState<null | "form" | "sent">(null);
  const [note, setNote] = useState("");
  const [phone, setPhone] = useState("");
  const [ticket, setTicket] = useState<{ id: string; no: number } | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const shown = ws !== "platform" && !!me;

  const load = useCallback(async () => { try { setStatus(await api<Status>("/api/ai/support/status")); } catch { setStatus(null); } }, []);
  useEffect(() => { if (shown) void load(); }, [shown, load]);
  useEffect(() => {
    // (feature guide) "Ask Senvia Assistant" opens the panel with the page context prefilled (the user finishes the question)
    const h = (e: Event) => { setOpen(true); const p = (e as CustomEvent<{ prefill?: string } | undefined>).detail?.prefill; if (typeof p === "string" && p) setInput((cur) => (cur.trim() ? cur : p)); };
    window.addEventListener("spos:support-open", h);
    return () => window.removeEventListener("spos:support-open", h);
  }, []);
  useEffect(() => { endRef.current?.scrollIntoView?.({ block: "end" }); }, [msgs, busy, human]);
  if (!shown || !me) return null;
  const available = !!status?.available;

  const send = async (textArg?: string) => {
    const text = (textArg ?? input).trim();
    if (!text || busy || !available) return;
    setInput(""); setMsgs((m) => [...m, { role: "user", text }]); setBusy(true);
    try {
      const r = await api<{ conversationId: string; reply: string; links: Array<{ id: string; title: string; route: string }>; clarifying: boolean; remainingToday: number }>("/api/ai/support/chat",
        { method: "POST", body: { message: text, lang: locale, page: loc.pathname, ...(conv ? { conversationId: conv } : {}) } });
      setConv(r.conversationId);
      setMsgs((m) => [...m, { role: "assistant", text: r.reply, links: r.links, clarifying: r.clarifying }]);
      setStatus((s) => (s ? { ...s, remainingToday: r.remainingToday } : s));
    } catch (e) { setMsgs((m) => [...m, { role: "error", text: errorText(e) }]); } finally { setBusy(false); }
  };
  const escalate = async () => {
    try {
      const r = await api<{ ticketId?: string; ticketNo?: number }>("/api/ai/support/escalate", { method: "POST", body: { ...(conv ? { conversationId: conv } : {}), ...(note.trim() ? { note: note.trim() } : {}), ...(phone.trim() ? { phone: phone.trim() } : {}), page: loc.pathname, lang: locale } });
      setHuman("sent"); setNote("");
      setTicket(r.ticketId && r.ticketNo ? { id: r.ticketId, no: r.ticketNo } : null);
      feedbackChanged();
    } catch (e) { setMsgs((m) => [...m, { role: "error", text: errorText(e) }]); setHuman(null); }
  };
  const routeOk = (route: string) => linkAllowed(me, route);

  return (
    <>
      <button type="button" className={"ai-fab hh-fab" + (available ? "" : " hh-fab-off")} onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span className="hh-fab-icon" aria-hidden>💬</span> <span className="hh-fab-label">{t("hh.sa.button")}</span>{status && !available ? <span className="hh-fab-tag">{t("hh.sa.unavailable")}</span> : null}
      </button>
      {open && (
        <aside className="ai-panel hh-support" role="dialog" aria-label={t("hh.sa.button")}>
          <header className="ai-head">
            <strong>💬 {t("hh.sa.button")}</strong>
            <span className="spacer" />
            <button className="btn btn-quiet btn-sm" onClick={() => { setMsgs([]); setConv(undefined); setHuman(null); }}>{t("hh.sa.new")}</button>
            <button className="btn btn-quiet btn-sm" aria-label={t("hh.sa.close")} onClick={() => setOpen(false)}>✕</button>
          </header>
          <div className="ai-body">
            {!available ? <div className="note">{t("hh.sa.unavailable")} — {t("hh.sa.unavailableLong")}</div> : (<>
              <p className="muted small">{t("hh.sa.intro")}</p>
              {msgs.length === 0 && (
                <div className="hh-suggest">
                  {(["hh.sa.suggest1", "hh.sa.suggest2", "hh.sa.suggest3"] as const).map((k) => <button key={k} type="button" className="chip" onClick={() => void send(t(k))}>{t(k)}</button>)}
                </div>
              )}
            </>)}
            {msgs.map((m, i) => (
              <div key={i} className={"ai-msg ai-" + (m.role === "error" ? "assistant" : m.role)}>
                {m.role === "user" && <div className="ai-bubble">{m.text}</div>}
                {m.role === "error" && <div className="ai-bubble hh-error">{m.text}</div>}
                {m.role === "assistant" && (<>
                  <div className={"ai-bubble" + (m.clarifying ? " hh-clarify" : "")}><HelpText text={m.text.replace(/\s*\[[a-z0-9-]{2,40}\]/g, "")} /></div>
                  {m.clarifying && <div className="muted small">{t("hh.sa.clarify")}</div>}
                  {!!m.links?.length && (
                    <div className="row ai-links">
                      {m.links.filter((l) => routeOk(l.route)).slice(0, 3).map((l) => <Link key={l.id} className="btn btn-quiet btn-sm" to={l.route} onClick={() => setOpen(false)}>{t("hh.sa.open")} → {l.title}</Link>)}
                    </div>
                  )}
                </>)}
              </div>
            ))}
            {busy && <div className="muted">{t("hh.sa.thinking")}</div>}
            {human === "form" && (
              <form className="card stack-sm hh-human" onSubmit={(e) => { e.preventDefault(); void escalate(); }}>
                <strong>{t("hh.sa.humanTitle")}</strong>
                <label className="field"><span>{t("hh.sa.humanNote")}</span><textarea rows={3} value={note} maxLength={2000} onChange={(e) => setNote(e.target.value)} /></label>
                <label className="field"><span>{t("hh.sa.humanPhone")}</span><input value={phone} maxLength={40} onChange={(e) => setPhone(e.target.value)} /></label>
                <button className="btn btn-primary btn-sm" type="submit">{t("hh.sa.humanSend")}</button>
              </form>
            )}
            {human === "sent" && (ticket
              ? <div className="note ok" data-testid="sa-ticket">{t("nn.saDone", { ref: feedbackRef(ticket.no) })}{canSeeFeedback(me) && <> <Link to={feedbackPath(ws) + "?ticket=" + ticket.id} onClick={() => setOpen(false)}>{t("nn.saView")} ›</Link></>}</div>
              : <div className="note ok">{t("hh.sa.humanDone")}</div>)}
            <div ref={endRef} />
          </div>
          <div className="hh-foot">
            <button type="button" className="btn btn-quiet btn-sm" onClick={() => setHuman(human === "form" ? null : "form")} disabled={human === "sent"}>🙋 {t("hh.sa.human")}</button>
            {canSeeFeedback(me) && <Link className="btn btn-quiet btn-sm" to={feedbackPath(ws) + "?from=" + encodeURIComponent(loc.pathname)} onClick={() => setOpen(false)} data-testid="sa-feedback">💬 {t("nn.saLink")}</Link>}
            {available && status && <span className="muted small">{t("hh.sa.remaining", { n: status.remainingToday })}</span>}
          </div>
          <form className="ai-input" onSubmit={(e) => { e.preventDefault(); void send(); }}>
            <textarea rows={2} value={input} data-testid="sa-input" autoFocus={!!input} maxLength={2000} placeholder={t("hh.sa.placeholder")} onChange={(e) => setInput(e.target.value)} disabled={!available}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void send(); } }} />
            <button className="btn btn-primary" type="submit" disabled={busy || !input.trim() || !available}>{t("hh.sa.send")}</button>
          </form>
        </aside>
      )}
    </>
  );
}
